import { makeAutoObservable, runInAction } from 'mobx';

import { User, UserWithName, RoleName, getUserRoleNames, Role } from '../models/User';
import {
	deleteUser, fetchDirectory, fetchRoles,
	fetchUsers, sync, updateUserStatus, updateUserRoles
} from '../services/UsersAPI';
import { FETCH_MESSAGE } from './Common';
import { currentUserStore } from './CurrentUser';
import { errorStore } from './ErrorStore';
import { Department } from '../models/Department';
import { departmentStore } from './DepartmentStore';
import { updateUserDepartments as updateUserDepartmentsRequest } from '../services/DepartmentsAPI';
import { getActiveDepartmentCode } from '../utils/ActiveDepartment';

class UserStore {
	data: User[] = [];
	directory: UserWithName[] = [];
	availableRoles: Role[] = [];
	availableDepartments: Department[] = [];
	isLoading = false;
	isLoadingRoles = false;
	isLoadingDepartments = false;
	isUpdating = false;
	currentUser: User | undefined;
	noRowsMessage = FETCH_MESSAGE;

	constructor() {
		makeAutoObservable(this);
	}

	async loadData() {
		if (this.data.length || this.isLoading) return;
		const departmentCode = getActiveDepartmentCode();

		try {
			this.setLoading(true);
			const response = await fetchUsers();
			if (departmentCode !== getActiveDepartmentCode()) {
				return;
			}

			this.setData(response || [], true);
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async reloadForActiveDepartment() {
		this.clearData();
		await this.loadData();
	}

	async loadDirectory() {
		if (this.directory.length || this.isLoading) return;

		try {
			this.setLoading(true);
			const response = await fetchDirectory();
			this.setDirectory(response || []);
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async loadAvailableRoles() {
		if (this.availableRoles.length || this.isLoadingRoles) return;

		try {
			this.setLoadingRoles(true);
			const response = await fetchRoles();
			this.setAvailableRoles(response || []);
			this.setLoadingRoles(false);
		} catch (error: any) {
			this.setError(error.message);
		}
	}

	async loadAvailableDepartments() {
		if (this.availableDepartments.length || this.isLoadingDepartments) return;

		try {
			this.setLoadingDepartments(true);
			await departmentStore.loadAvailableDepartments();
			this.setAvailableDepartments(departmentStore.availableDepartments);
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoadingDepartments(false);
		}
	}

	async syncUser() {
		try {
			this.setLoading(true);
			const userResponse = await sync();
			this.setCurrentUser(userResponse);
			await departmentStore.initialize(userResponse);
		} catch (error: any) {
			this.clearCurrentUser();
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async updateUserDepartments(userId: string, departmentIds: number[], onSuccess?: () => void) {
		this.setUpdating(true);

		try {
			await updateUserDepartmentsRequest(userId, departmentIds);
			runInAction(() => this.applyDepartmentsUpdate(userId, departmentIds));
			if (onSuccess) onSuccess();
		} catch (err: any) {
			this.setError(err.message || 'Failed to update user departments');
		} finally {
			this.setUpdating(false);
		}
	}

	async updateUser(userId: string, roleIds: number[], onSuccess?: () => void) {
		this.setUpdating(true);

		try {
			const updatedUser = await updateUserRoles(userId, roleIds);
			runInAction(() => this.applyRolesUpdate(userId, updatedUser));
			if (onSuccess) onSuccess();
		} catch (err: any) {
			this.setError(err.message || 'Failed to update user roles');
		} finally {
			this.setUpdating(false);
		}
	}

	async updateStatus(userId: string, isActive: boolean) {
		this.setUpdating(true);

		try {
			await updateUserStatus(userId, isActive);
			runInAction(() => {
				const index = this.data.findIndex(row => row.id === userId);
				if (index >= 0) {
					this.data[index] = { ...this.data[index], isActive };
				}
			});
		} catch (err: any) {
			this.setError(err.message || 'Failed to update user.');
			throw err;
		} finally {
			this.setUpdating(false);
		}
	}

	async delete(user: User) {
		this.setUpdating(true);

		try {
			await deleteUser(user.id);
			runInAction(() => {
				this.data = this.data.filter(row => row.id !== user.id);
			});
		} catch (err: any) {
			this.setError(err.message || 'Failed to delete user.');
		} finally {
			this.setUpdating(false);
		}
	}

	private get roles() {
		return getUserRoleNames(this.currentUser);
	}

	get isAdmin() {
		return this.roles.includes(RoleName.Admin);
	}

	get isUserAdmin() {
		return this.isAdmin || this.roles.includes(RoleName.UserAdmin) || this.roles.includes(RoleName.DepartmentAdmin);
	}

	get isApprover() {
		return this.isAdmin || this.roles.includes(RoleName.Approver);
	}

	get canSeePostSplit() {
		return this.isApprover || this.roles.includes(RoleName.InvoiceEntry);
	}

	get canRequestApproval() {
		return this.isAdmin || this.roles.includes(RoleName.InvoiceEntry) || this.roles.includes(RoleName.Spender);
	}

	get isInvoiceEntry() {
		return this.isApprover || this.canRequestApproval;
	}

	private applyRolesUpdate(userId: string, updatedUser: User) {
		this.data = this.data.map(user =>
			user.id === userId
				? { ...user, ...updatedUser }
				: user
		);
		if (this.currentUser?.id === userId) {
			this.setCurrentUser({ ...this.currentUser, ...updatedUser });
		}
	}

	private applyDepartmentsUpdate(userId: string, departmentIds: number[]) {
		const departmentMemberships = departmentIds.map((departmentId, index) => ({
			userId,
			departmentId,
			isDefault: index === 0,
			isActive: true,
			department: departmentStore.availableDepartments.find(department => department.id === departmentId),
		}));

		this.data = this.data.map(user =>
			user.id === userId
				? { ...user, departments: departmentMemberships, userDepartments: departmentMemberships }
				: user
		);

		if (this.currentUser?.id === userId) {
			this.setCurrentUser({ ...this.currentUser, departments: departmentMemberships, userDepartments: departmentMemberships });
			void departmentStore.initialize(this.currentUser);
		}
	}

	UserName(id?: string) {
		return this.directory.find(row => row.id === id)?.name;
	}

	setLoading(loading: boolean) {
		this.isLoading = loading;
	}

	setLoadingRoles(loading: boolean) {
		this.isLoadingRoles = loading;
	}

	setLoadingDepartments(loading: boolean) {
		this.isLoadingDepartments = loading;
	}

	setUpdating(updating: boolean) {
		this.isUpdating = updating;
	}

	setError(error: string) {
		errorStore.report(error);
	}

	setData(users: User[], dataLoaded: boolean = false) {
		this.data = users;

		if (this.data.length === 0) {
			this.noRowsMessage = dataLoaded ? 'No users found.' : FETCH_MESSAGE;
		}
	}

	setDirectory(users: UserWithName[]) {
		this.directory = users;
	}

	setAvailableRoles(roles: Role[]) {
		this.availableRoles = roles;
	}

	setAvailableDepartments(departments: Department[]) {
		this.availableDepartments = departments;
	}

	setCurrentUser(user: User) {
		this.currentUser = user;
		currentUserStore.setUser(user);
		departmentStore.currentUser = user;
	}

	clearCurrentUser() {
		this.currentUser = undefined;
		currentUserStore.setUser(undefined);
		departmentStore.currentUser = undefined;
	}

	clearData() {
		this.data = [];
		this.noRowsMessage = FETCH_MESSAGE;
	}
}

export const userStore = new UserStore();
